import { HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { environment } from '@env/environment';
import { delay, Observable, of } from 'rxjs';
import {
  mockAlertConfiguration,
  mockCompanies,
  mockCountries,
  mockGroups,
  mockIssues,
  mockLogs,
  mockNews,
  mockNotifications,
  mockRoles,
  mockTrips,
  mockUserInfo,
  mockUsers,
} from '../mock/mock-data';

const languages = [
  { id: 'lang-en', name: 'English' },
  { id: 'lang-ja', name: 'Japanese' },
];
const positions = [
  { id: 'position-manager', name: 'Manager' },
  { id: 'position-engineer', name: 'Engineer' },
  { id: 'position-admin', name: 'Administrator' },
];
const nationalities = mockCountries.map(country => ({ ...country }));

const success = (data: any = null) => ({ code: '200', data, msg: 'Mock request completed' });
const paged = (items: any[], req: HttpRequest<unknown>) => {
  const url = new URL(req.urlWithParams);
  const param = (name: string) => req.params.get(name) || url.searchParams.get(name);
  const page = Number(param('page') || 1);
  const count = Number(param('count') || 10);
  const search = (param('search') || param('name') || '').toLowerCase();
  const filtered = search
    ? items.filter(item => JSON.stringify(item).toLowerCase().includes(search))
    : items;
  const start = (page - 1) * count;
  return { page, count, total_count: filtered.length, items: filtered.slice(start, start + count) };
};
const optionResult = (items: any[], totalKey: string) => ({
  result: items,
  [totalKey]: items.length,
});
const response = (body: any): Observable<any> =>
  of(new HttpResponse({ status: 200, body })).pipe(delay(120));

function idFromUrl(url: string): string {
  const segments = url.split('/');
  for (let index = segments.length - 1; index >= 0; index--) {
    if (segments[index]) return segments[index].split('?')[0];
  }
  return '';
}

function userForDetail(id: string): any {
  return mockUsers.find(user => user.id === id) || mockUsers[0];
}

function handleCompanyMutation(req: HttpRequest<any>, path: string): void {
  if (path.includes('/company-service/createCompany')) {
    const country = mockCountries.find(item => item.id === req.body?.countryid);
    mockCompanies.unshift({
      ...req.body,
      id: `company-${Date.now()}`,
      country: country?.name || '',
      created_at: new Date(),
      modified_at: new Date(),
    });
  }
  if (path.includes('/company-service/updateCompany/')) {
    const id = idFromUrl(path);
    const index = mockCompanies.findIndex(item => item.id === id);
    const company = mockCompanies.at(index);
    if (index >= 0 && company) mockCompanies.splice(index, 1, { ...company, ...req.body });
  }
  if (path.includes('/company-service/deleteCompany/')) {
    const index = mockCompanies.findIndex(item => item.id === idFromUrl(path));
    if (index >= 0) mockCompanies.splice(index, 1);
  }
}

function handleGroupMutation(req: HttpRequest<any>, path: string): void {
  if (path.includes('/group-service/createGroup')) {
    mockGroups.unshift({
      id: `group-${Date.now()}`,
      name: req.body?.name || 'Mock Group',
      totalUsers: req.body?.userIds?.length || 0,
      totalTrips: 0,
      totalEmergencyAlerts: 0,
    });
  }
  if (path.includes('/group-service/updateGroup/')) {
    const index = mockGroups.findIndex(item => item.id === idFromUrl(path));
    const group = mockGroups.at(index);
    if (index >= 0 && group) mockGroups.splice(index, 1, { ...group, name: req.body?.name });
  }
  if (path.includes('/group-service/deleteGroup/')) {
    const index = mockGroups.findIndex(item => item.id === idFromUrl(path));
    if (index >= 0) mockGroups.splice(index, 1);
  }
  if (path.includes('/group-service/bulkDeleteGroups')) {
    const ids = new Set(req.body?.groupIds || []);
    for (let index = mockGroups.length - 1; index >= 0; index--) {
      const group = mockGroups.at(index);
      if (group && ids.has(group.id)) mockGroups.splice(index, 1);
    }
  }
}

function deleteMockUsers(ids: Set<unknown>): void {
  for (let index = mockUsers.length - 1; index >= 0; index--) {
    const user = mockUsers.at(index);
    if (user && ids.has(user.id)) mockUsers.splice(index, 1);
  }
}

function handleUserMutation(req: HttpRequest<any>, path: string): void {
  if (path.includes('/user-service/createUser')) {
    mockUsers.unshift({
      ...mockUserInfo,
      ...req.body,
      id: `user-${Date.now()}`,
      fullname: `${req.body?.firstname || ''} ${req.body?.lastname || ''}`.trim(),
      usercode: `MOCK-${mockUsers.length + 1}`,
    });
  }
  if (path.includes('/user-service/updateUser/')) {
    const index = mockUsers.findIndex(item => item.id === idFromUrl(path));
    const user = mockUsers.at(index);
    if (index >= 0 && user) mockUsers.splice(index, 1, { ...user, ...req.body });
  }
  if (path.includes('/user-service/deleteUser/')) {
    const index = mockUsers.findIndex(item => item.id === idFromUrl(path));
    if (index >= 0) mockUsers.splice(index, 1);
  }
  if (path.includes('/user-service/bulkDeleteUsers')) {
    const ids = new Set(req.body?.userIds || []);
    deleteMockUsers(ids);
  }
  if (path.includes('/user-service/setEmergencyContact')) {
    const ids = new Set(req.body?.userIds || []);
    mockUsers.forEach(user => {
      if (ids.has(user.id)) user.isemergencycontact = Boolean(req.body?.isEmergencyContact);
    });
  }
}

function handleRoleMutation(req: HttpRequest<any>, path: string): void {
  if (path.includes('/role-service/assignUsersToRole')) {
    const role = mockRoles.find(item => item.id === req.body?.roleId);
    const ids = new Set(req.body?.userIds || []);
    mockUsers.forEach(user => {
      if (ids.has(user.id) && role) {
        user.roleid = role.id;
        user.role = role.name;
      }
    });
  }
}

function handleTripMutation(req: HttpRequest<any>, path: string): void {
  if (path.includes('/trip-service/delete_trip_completed')) {
    const id = req.params.get('trip_id');
    const index = mockTrips.findIndex(item => item.id === id);
    if (index >= 0) mockTrips.splice(index, 1);
  }
}

function handleConfigurationMutation(req: HttpRequest<any>, path: string): void {
  if (path.includes('/configuration-service/updateAlertConfiguration')) {
    mockAlertConfiguration.arrivalReminderFreq = Number(
      req.body?.arrivalReminderFreq ?? mockAlertConfiguration.arrivalReminderFreq,
    );
    mockAlertConfiguration.alertReminderFreq = Number(
      req.body?.alertReminderFreq ?? mockAlertConfiguration.alertReminderFreq,
    );
    mockAlertConfiguration.gpsFetchInterval = Number(
      req.body?.gpsFetchInterval ?? mockAlertConfiguration.gpsFetchInterval,
    );
    mockAlertConfiguration.keywords = String(req.body?.keywords ?? mockAlertConfiguration.keywords);
  }
}

function handleMutation(req: HttpRequest<any>, path: string): any {
  handleCompanyMutation(req, path);
  handleGroupMutation(req, path);
  handleUserMutation(req, path);
  handleRoleMutation(req, path);
  handleTripMutation(req, path);
  handleConfigurationMutation(req, path);

  return success({ ok: true });
}

function mockAuthenticationGetBody(req: HttpRequest<any>, path: string): any {
  if (path.endsWith('/user-service/getMyInfo')) return success(mockUserInfo);
  if (path.includes('/authenticate-service/refresh-entra-token')) {
    return success({ access: 'mock-data-token' });
  }
  if (path.includes('/language-service/getAllLanguages')) {
    return success(paged(languages, req));
  }
  if (path.includes('/authenticate-service/getAllNationalities')) {
    return success(paged(nationalities, req));
  }

  return undefined;
}

function mockTripGetBody(req: HttpRequest<any>, path: string): any {
  if (path.includes('/trip-service/manage_trips_detail')) {
    const userId = req.params.get('user_id') || '';
    const tripId = req.params.get('trip_id') || '';
    const user = userForDetail(userId);
    return {
      result: [
        {
          ...user,
          name: user.fullname,
          company_name: user.company,
          country_name: user.country,
          trips: mockTrips.filter(trip => trip.id === tripId || trip.user_id === userId),
        },
      ],
    };
  }
  if (path.includes('/trip-service/manage_trips')) {
    const data = paged(mockTrips, req);
    return {
      result: data.items,
      paging: { count: data.count, page: data.page, total: data.total_count },
      total: {
        total_users: new Set(mockTrips.map(item => item.user_id)).size,
        total_trips: mockTrips.length,
        total_current_trips: mockTrips.filter(item => item.status === 'ONGOING').length,
        total_issues: mockIssues.length,
      },
    };
  }
  if (path.includes('/trip-service/get_user_trip_history_detail')) {
    return {
      result: {
        created_at: mockTrips[0]?.created_at,
        email: mockTrips[0]?.email,
        groups: mockTrips[0]?.groups,
        status: mockTrips[0]?.status,
        trip_destinations: mockTrips[0]?.destinations.length || 0,
        trip_flight_ticket: 1,
        updated_at: new Date(),
        trips: mockTrips.map(trip => ({ trip_id: trip.id, destinations: trip.destinations })),
      },
    };
  }
  if (path.includes('/trip-service/get_user_trip_history')) {
    const userId = req.params.get('user_id') || mockUsers[0].id;
    const user = userForDetail(userId);
    const trips = mockTrips
      .filter(trip => trip.user_id === userId)
      .map(trip => ({
        status: trip.status,
        trip_history_id: `history-${trip.id}`,
        label: trip.label,
        trip_flight_ticket: trip.destinations.length,
        trip_id: trip.id,
        trip_total_emergency_alert: mockIssues.length,
        trip_destinations: trip.destinations.length,
        start_time: trip.destinations[0]?.scheduled_departure,
        end_time: trip.destinations.at(-1)?.scheduled_arrival,
      }));
    return {
      result: { ...user, full_name: user.fullname, user_id: user.id, trips },
      paging: { count: trips.length, page: 1, total: trips.length },
    };
  }
  if (path.includes('/trip-service/get_all_alerts_of_user')) {
    return {
      result: mockIssues,
      paging: { count: 10, page: 1, total: mockIssues.length },
      total_alerts: mockIssues.length,
      code: 200,
    };
  }
  if (path.includes('/trip-service/user_gps_location')) {
    return {
      code: 200,
      result: mockTrips.map((trip, index) => ({
        current_country: trip.current_country,
        fullname: trip.full_name,
        safety: trip.confirm_safety !== false,
        trip_id: trip.id,
        updated_at: new Date().toISOString(),
        user_id: trip.user_id,
        latitude: [13.7563, 1.3521, 35.6762][index] || 35.6762,
        longitude: [100.5018, 103.8198, 139.6503][index] || 139.6503,
      })),
    };
  }
  if (path.includes('/trip-service/get_client_url')) return { url: '' };

  return undefined;
}

function mockGroupGetBody(req: HttpRequest<any>, path: string): any {
  if (path.includes('/group-service/getAllGroupsWithStat')) {
    return success(paged(mockGroups, req));
  }
  if (path.includes('/group-service/getUsersInGroup/')) {
    const groupId = idFromUrl(path);
    const users = mockUsers.filter(user => user.groups?.some(group => group.id === groupId));
    return success(paged(users, req));
  }
  if (path.includes('/group-service/getUnassignedUsers/')) {
    return success(paged(mockUsers, req));
  }

  return undefined;
}

function mockUserGetBody(req: HttpRequest<any>, path: string): any {
  if (path.includes('/user-service/getUserById/')) {
    return success(userForDetail(idFromUrl(path)));
  }
  if (path.includes('/user-service/getUsersNotOnTrips')) {
    const tripUserIds = new Set(mockTrips.map(trip => trip.user_id));
    return success(
      paged(
        mockUsers.filter(user => !tripUserIds.has(user.id)),
        req,
      ),
    );
  }
  if (path.includes('/user-service/getUsersForIncidentNews')) {
    return success(paged(mockUsers, req));
  }
  if (path.includes('/user-service/getEmergencyContacts')) {
    return success(
      paged(
        mockUsers.filter(user => user.isemergencycontact),
        req,
      ),
    );
  }
  if (path.includes('/user-service/getAllUsers')) return success(paged(mockUsers, req));

  return undefined;
}

function mockCompanyAndRoleGetBody(req: HttpRequest<any>, path: string): any {
  if (path.includes('/company-service/getCompanyById/')) {
    return success(mockCompanies.find(item => item.id === idFromUrl(path)) || mockCompanies[0]);
  }
  if (path.includes('/company-service/getAllCompanies')) {
    return success(paged(mockCompanies, req));
  }
  if (path.includes('/role-service/getUsersByRoleId/')) {
    const roleId = idFromUrl(path);
    return success(
      paged(
        mockUsers.filter(user => user.roleid === roleId),
        req,
      ),
    );
  }
  if (path.includes('/role-service/getUsersNotInRole/')) {
    const roleId = idFromUrl(path);
    return success(
      paged(
        mockUsers.filter(user => user.roleid !== roleId),
        req,
      ),
    );
  }
  if (path.includes('/role-service/getAllRoles')) return success(paged(mockRoles, req));

  return undefined;
}

function mockLookupGetBody(path: string): any {
  if (path.includes('/lookup-service/get_all_country_for_news')) {
    return optionResult(
      mockCountries.map(item => item.name),
      'total_countries',
    );
  }
  if (path.includes('/lookup-service/get_all_company')) {
    return optionResult(
      mockCompanies.map(({ id, name }) => ({ id, name })),
      'total_companies',
    );
  }
  if (path.includes('/lookup-service/get_all_current_country')) {
    return optionResult(mockCountries, 'total_countries');
  }
  if (path.includes('/lookup-service/get_all_country')) {
    return optionResult(mockCountries, 'total_countries');
  }
  if (path.includes('/lookup-service/get_all_group')) {
    return optionResult(
      mockGroups.map(({ id, name }) => ({ id, name })),
      'total_groups',
    );
  }
  if (path.includes('/lookup-service/get_all_position')) {
    return optionResult(positions, 'total_positions');
  }
  if (path.includes('/lookup-service/get_all_role')) {
    return optionResult(
      mockRoles.map(({ id, name }) => ({ id, name })),
      'total_roles',
    );
  }

  return undefined;
}

function mockAuxiliaryGetBody(req: HttpRequest<any>, url: URL, path: string): any {
  if (path.includes('/news-service/category_news')) {
    const country = url.searchParams.get('userCountry') || url.searchParams.get('country');
    const body = url.searchParams.get('body');
    const articles = mockNews.filter(
      item => (!country || item.country === country) && (!body || item.title.includes(body)),
    );
    return { result: { articles, totalResults: articles.length } };
  }
  if (path.includes('/notification-service/get_user_notification')) {
    return {
      result: mockNotifications,
      is_read_all: mockNotifications.every(item => item.is_read),
    };
  }
  if (path.includes('/audit-service/exportAuditLogs')) {
    return { data: btoa('Mock log export'), msg: 'Mock log exported' };
  }
  if (path.includes('/audit-service/getAllLogs')) return success(paged(mockLogs, req));
  if (path.includes('/configuration-service/getAlertConfiguration')) {
    return success(mockAlertConfiguration);
  }

  return undefined;
}

function mockBody(req: HttpRequest<any>): any {
  const url = new URL(req.urlWithParams);
  const path = url.pathname;

  if (req.method !== 'GET') {
    if (path.includes('/authenticate-service/logout')) return success({});
    return handleMutation(req, path);
  }

  const authenticationBody = mockAuthenticationGetBody(req, path);
  if (authenticationBody !== undefined) return authenticationBody;

  const tripBody = mockTripGetBody(req, path);
  if (tripBody !== undefined) return tripBody;

  const groupBody = mockGroupGetBody(req, path);
  if (groupBody !== undefined) return groupBody;

  const userBody = mockUserGetBody(req, path);
  if (userBody !== undefined) return userBody;

  const companyAndRoleBody = mockCompanyAndRoleGetBody(req, path);
  if (companyAndRoleBody !== undefined) return companyAndRoleBody;

  const lookupBody = mockLookupGetBody(path);
  if (lookupBody !== undefined) return lookupBody;

  const auxiliaryBody = mockAuxiliaryGetBody(req, url, path);
  if (auxiliaryBody !== undefined) return auxiliaryBody;

  console.warn(`[MOCK_DATA] No fixture for ${req.method} ${path}`);
  return success({ items: [], total_count: 0, result: [] });
}

export const MockDataInterceptor: HttpInterceptorFn = (req, next) => {
  if (!environment.MOCK_DATA || !req.url.startsWith(environment.API_URL)) {
    return next(req);
  }
  return response(mockBody(req));
};
